import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, tap } from 'rxjs';

import { OFFER_SERVER_URL } from '../../config';
import { DataWithTranslation, TranslationType } from '../../shared/models/data-with-translations';
import {
	UfoProductSubmissionResponse,
	ProductType,
	PRODUCT_TYPE_A2C,
	TemplateVariableValues,
	TemplateSearchResponse,
	UfoProductSimulationResponse,
	UfoProductSimulationRequest,
	UfoProductSubmissionRequest
} from '../models/product';
import { ProductConfiguration } from '../models/product-configuration';

import { ProductTranslationService } from './product-translation.service';

const CONFIGURATIONS_URL = `${ OFFER_SERVER_URL }/configurations`;
const CONFIGURATIONS_URL_V1 = `${ OFFER_SERVER_URL }/v1/configurations`;
const CONFIGURATIONS_SUBMIT_URL = `${ OFFER_SERVER_URL }/configurations:submit`;
const CONFIGURATIONS_SIMULATE_URL = `${ OFFER_SERVER_URL }/configurations:simulate`;

const PRODUCT_TEMPLATE_URL = `${ OFFER_SERVER_URL }/templates`;
const TEMPLATE_SEARCH_URL = `${ OFFER_SERVER_URL }/templates:search`;

@Injectable({ providedIn: 'root' })
export class ConfigurationService {
	constructor(
		private readonly http: HttpClient,
		private readonly productTranslationService: ProductTranslationService
	) {}

	getMappedConfiguration(
		productReference: string,
		translationType?: TranslationType
	) {
		const productUrl = new URL(CONFIGURATIONS_URL);
		const params = productUrl.searchParams;
		params.append('productReference', productReference);
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<DataWithTranslation<ProductConfiguration>>(productUrl.toString()).pipe(map(
			product => {
				this.productTranslationService.add(product.translations);
				return product.data;
			}
		));
	}

	getConfiguration(
		configurationReference: string,
		translationType?: TranslationType
	) {
		const productUrl = new URL(`${ CONFIGURATIONS_URL }/${ configurationReference }`);
		const params = productUrl.searchParams;
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<DataWithTranslation<ProductConfiguration>>(productUrl.toString()).pipe(map(
			product => {
				this.productTranslationService.add(product.translations);
				return product.data;
			}
		));
	}

	getConfigurationById(
		id: string,
		translationType?: TranslationType
	) {
		const productUrl = new URL(`${ CONFIGURATIONS_URL_V1 }/${ id }`);
		const params = productUrl.searchParams;
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<DataWithTranslation<ProductConfiguration>>(productUrl.toString()).pipe(map(
			product => {
				this.productTranslationService.add(product.translations);
				return product.data;
			}
		));
	}

	submitTemplateProduct(
		templateProductSubmission: UfoProductSubmissionRequest,
		serviceType: string,
		translationType?: TranslationType,
	) {
		const url = new URL(CONFIGURATIONS_SUBMIT_URL);
		const params = url.searchParams;
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		params.append('serviceType', String(serviceType));
		return this.http.post<DataWithTranslation<UfoProductSubmissionResponse>>(
			url.toString(),
			templateProductSubmission
		).pipe(
			tap(
				rsp => {
					this.productTranslationService.add(rsp.translations);
				}
			),
			map(
				rsp => rsp.data
			)
		);
	}

	simulateTemplateProduct(
		templateSimulationRequest: UfoProductSimulationRequest,
		translationType?: TranslationType
	) {
		const url = new URL(CONFIGURATIONS_SIMULATE_URL);
		const params = url.searchParams;
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.post<DataWithTranslation<UfoProductSimulationResponse>>(
			url.toString(),
			templateSimulationRequest
		).pipe(
			tap(
				rsp => {
					this.productTranslationService.add(rsp.translations);
				}
			),
			map(
				rsp => rsp.data
			)
		);
	}

	getConfigurationMappedOrNot(
		productId: string,
		productType: ProductType,
		translationType: TranslationType
	) {
		if (
			productType === PRODUCT_TYPE_A2C
		) {
			return this.getMappedConfiguration(
				productId,
				translationType
			);
		}
		return this.getConfiguration(
			productId,
			translationType
		);
	}

	getVariableValues(
		templateNumber: number,
		variableName: string
	) {
		const url = new URL(`${ PRODUCT_TEMPLATE_URL }/${ templateNumber }/${ variableName }`);
		return this.http.get<TemplateVariableValues>(url.toString());
	}

	searchTemplate(
		productId: string,
		withProduct: boolean,
		translationType?: TranslationType
	) {
		const url = new URL(TEMPLATE_SEARCH_URL);
		const params = url.searchParams;
		params.append('productId', productId);
		params.append('withProduct', String(withProduct));
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<DataWithTranslation<TemplateSearchResponse>>(url.toString())
			.pipe(
				tap(templateSearchResponse => {
					this.productTranslationService.add(templateSearchResponse.translations);
				}),
				map(templateSearchResponse => templateSearchResponse.data)
			);
	}
}
